// ─────────────────────────────────────────────────────────────────────────
// Agent Tutor — API client
//
// Agents live in the database (app/routes/agents.py). This module used to be a
// localStorage stand-in, which meant an agent only existed in the browser
// profile that created it — a teacher publishing on their machine was invisible
// to every student — and because it keyed agents by one fixed id, building a
// second agent silently overwrote the first. Both problems are gone now that
// the server owns agent identity.
//
// Every function here is async. Callers must await.
// ─────────────────────────────────────────────────────────────────────────

import config from '../config';

const jsonHeaders = { 'Content-Type': 'application/json' };

async function request(path, options = {}) {
  const res = await fetch(`${config.API_BASE_URL}/api${path}`, {
    credentials: 'include',
    ...options,
  });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch (e) {
      // non-JSON error body — keep the status-code message
    }
    throw new Error(detail);
  }
  if (res.status === 204) return null;
  return res.json();
}

export const HINT_STYLES = [
  {
    value: 'socratic',
    label: 'Socratic — ask guiding questions',
    example: '"What do you know about the two sides of this equation?"',
  },
  {
    value: 'nudge',
    label: 'Nudge — reveal one small step at a time',
    example: '"Try isolating x first. What could you subtract from both sides?"',
  },
  {
    value: 'worked_example',
    label: 'Worked example — show a similar solved problem',
    example: 'Shows a parallel example, never the actual answer.',
  },
];

export const TONE_OPTIONS = [
  { value: 'warm', label: 'Warm & playful' },
  { value: 'encouraging', label: 'Encouraging' },
  { value: 'neutral', label: 'Neutral & focused' },
];

// NOTE: labelled "How strictly should it grade?" in the mockup, but this
// only ever changes the *wording* of feedback the agent gives — it does not
// compute a grade or score. Actual grading/rubric scoring is out of the
// current SOW scope (see Math_Tutor_Product_Analysis.md, Section 8).
export const FEEDBACK_STYLES = [
  { value: 'encouraging', label: 'Encouraging — reward effort and progress', example: '"Great improvement! Let’s tighten up step 2 together."' },
  { value: 'balanced', label: 'Balanced — honest but kind', example: '"Solid reasoning, but the sign error cost you a step."' },
  { value: 'rubric', label: 'By-the-rubric — exam style', example: '"Sign error in step 2. Units missing in final answer."' },
];

export const PACING_OPTIONS = [
  { value: 'mastery', label: 'Mastery-based — advance after 3 correct in a row', hint: 'Reteaches with new examples when a student struggles.' },
  { value: 'schedule', label: 'Follow class schedule', hint: 'Moves at the pace of your weekly class plan.' },
];

export const GRADE_BANDS = ['K-2', '3-5', '6-8', '9-12'];

// Student/teacher-facing wording for each GRADE_BANDS entry — ordinal words,
// not the raw "K-2"/"3-5" shorthand, since that shorthand is this feature's
// internal vocabulary (see AgentStyle.gradeBandOverride in
// app/routes/agent_tutor.py), not language a student would recognize as an
// answer to "what grade are you in?". Used by GradeBandPicker's tiles and
// ProblemBar's "Explaining at a ___ grade level" badge, so both surfaces
// describe the same band identically rather than drifting.
export const GRADE_BAND_LABELS = {
  'K-2': 'Kindergarten–2nd',
  '3-5': '3rd–5th',
  '6-8': '6th–8th',
  '9-12': '9th–12th',
};

export const SUBJECTS = [
  { value: 'math', label: 'Math', enabled: true },
  { value: 'science', label: 'Science', enabled: false },
  { value: 'history', label: 'History', enabled: false },
  { value: 'languages', label: 'Languages', enabled: false },
  { value: 'english', label: 'English', enabled: false },
  { value: 'arts', label: 'Arts', enabled: false },
  { value: 'computing', label: 'Computing', enabled: false },
  { value: 'other', label: 'Other', enabled: false },
];

// A brand-new agent has no id — the server assigns one on POST. This is what
// makes "Build Another Agent" create rather than overwrite.
export function defaultAgentDraft(currentUser) {
  return {
    id: null,
    status: 'draft',
    name: '',
    subject: 'math',
    grades: [],
    startMode: 'fresh',
    hintStyle: 'socratic',
    tone: 'encouraging',
    feedbackStyle: 'balanced',
    pacing: 'mastery',
    activities: {
      stepByStep: true,
      flashcards: false,
      quizzes: false,
      practiceTests: false,
    },
    misconceptions: [],
    materialIds: [],
    assignedGroupIds: [],
    shareToMarketplace: false,
    welcomeMessage: '',
    tutoringRules: '',
    createdBy: currentUser?.username || null,
    lastEditedBy: currentUser?.username || null,
  };
}

export async function getAgent(agentId) {
  if (!agentId) return null;
  try {
    return await request(`/agents/${agentId}`);
  } catch (e) {
    return null;
  }
}

/** Agents the current teacher/admin can manage. */
export async function listAllAgents() {
  return request('/agents');
}

/**
 * Agents published to a class the current student belongs to. Scoped
 * server-side to the caller's own memberships — no group ids needed here.
 */
export async function listAssignedAgents() {
  return request('/agents/assigned');
}

/**
 * Create or update an agent.
 *
 * `publish` is explicit rather than inferred: passing false must leave an
 * already-active agent active (so editing a live agent doesn't unpublish it),
 * while never promoting a draft. Callers that only mean "save my edits" should
 * pass the agent's current status, not `true`.
 */
export async function saveAgent(agent, { publish = false, currentUser } = {}) {
  const status = publish || agent.status === 'active' ? 'active' : 'draft';
  const body = JSON.stringify({
    name: agent.name,
    subject: agent.subject,
    grades: agent.grades || [],
    startMode: agent.startMode,
    hintStyle: agent.hintStyle,
    tone: agent.tone,
    feedbackStyle: agent.feedbackStyle,
    pacing: agent.pacing,
    activities: agent.activities || {},
    misconceptions: (agent.misconceptions || []).map((m) => ({
      mistake: m.mistake,
      response: m.response || '',
    })),
    materialIds: agent.materialIds || [],
    assignedGroupIds: agent.assignedGroupIds || [],
    shareToMarketplace: !!agent.shareToMarketplace,
    welcomeMessage: agent.welcomeMessage || '',
    tutoringRules: agent.tutoringRules || '',
    status,
  });

  if (agent.id) {
    return request(`/agents/${agent.id}`, { method: 'PUT', headers: jsonHeaders, body });
  }
  return request('/agents', { method: 'POST', headers: jsonHeaders, body });
}

export async function deleteAgent(agentId) {
  return request(`/agents/${agentId}`, { method: 'DELETE' });
}

// ─────────────────────────────────────────────────────────────────────────
// TutorWorkspace endpoints (start-problem / check-step / hint)
//
// These are new and may not be deployed yet. Every function here throws an
// Error with `.offline = true` on a 404 or network failure specifically —
// that's TutorWorkspace's signal to fall back to the local demo logic in
// mathTutorDemoProblems.js instead of leaving the pad broken. Any other
// non-2xx response is a real bug and is thrown as a normal Error so it
// isn't silently swallowed forever.
// ─────────────────────────────────────────────────────────────────────────

async function tutorPost(path, body) {
  let res;
  try {
    res = await fetch(`${config.API_BASE_URL}/api/agent-tutor${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
  } catch (e) {
    const err = new Error('Could not reach the tutor service.');
    err.offline = true;
    throw err;
  }
  if (res.status === 404) {
    const err = new Error('This tutor endpoint is not deployed yet.');
    err.offline = true;
    throw err;
  }
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body2 = await res.json();
      if (Array.isArray(body2?.detail)) {
        // FastAPI 422 validation errors: an array of {loc, msg, type}, not a
        // string. Interpolating the array directly stringifies each item via
        // Object.prototype.toString and renders as literal "[object Object]".
        const msgs = body2.detail.map((d) => (d && typeof d === 'object' ? d.msg || JSON.stringify(d) : String(d)));
        if (msgs.length) detail = msgs.join('; ');
      } else if (typeof body2?.detail === 'string' && body2.detail) {
        detail = body2.detail;
      } else if (body2?.detail) {
        detail = JSON.stringify(body2.detail);
      }
    } catch (e) {
      // non-JSON error body — keep the status-code message
    }
    const err = new Error(detail);
    err.offline = res.status >= 500; // treat server outages as a fallback case too
    throw err;
  }
  return res.json();
}

/**
 * `problemText` is the raw string the student typed — the backend's
 * `StartProblemRequest.problem` field is a plain string, unlike check-step
 * and hint below, whose `problem` is the parsed `{display, latex, goal}`
 * ref. Don't "fix" this by making the three uniform; they're deliberately
 * different shapes (see agent_tutor.py).
 *
 * `sessionId` resumes an existing session's style (see StartProblemRequest);
 * `agentId` starts a brand-new persisted session for that agent. Passing
 * neither (student-preview/embedded test-drive) behaves exactly as before —
 * nothing is persisted. Never pass both.
 *
 * Returns { problem: {raw, display, latex, topic, difficulty, supported, reason}, goal, expectedSteps, opening, starterHint, sessionId? }
 */
export async function startProblem(agent, problemText, { sessionId, agentId } = {}) {
  return tutorPost('/start-problem', { agent, problem: problemText, sessionId, agentId });
}

/** { verdict, isFinal, solved, feedback, stepLatex, misconception, nextPrompt, hint } */
export async function checkStep(agent, problem, stepsSoFar, studentStep, hintsUsed, sessionId) {
  return tutorPost('/check-step', { agent, problem, stepsSoFar, studentStep, hintsUsed, sessionId });
}

/** { level, hint, hintLatex, isFullStep } */
export async function getHint(agent, problem, stepsSoFar, level, sessionId) {
  return tutorPost('/hint', { agent, problem, stepsSoFar, level, sessionId });
}

// ─────────────────────────────────────────────────────────────────────────
// TutorSession read/abandon endpoints (app/routes/tutor_sessions.py)
//
// Same offline convention as the block above: a 404 or network failure sets
// `.offline = true` so a caller can fall back to local/offline behavior
// (here, that just means "resume nothing" — a session fetch failing must
// never leave a student stuck, only skip the resume-notice/continue chip).
// Any other non-2xx is a real bug and surfaces as a normal Error.
// ─────────────────────────────────────────────────────────────────────────

async function tutorGet(path) {
  let res;
  try {
    res = await fetch(`${config.API_BASE_URL}/api/agent-tutor${path}`, {
      credentials: 'include',
    });
  } catch (e) {
    const err = new Error('Could not reach the tutor service.');
    err.offline = true;
    throw err;
  }
  if (res.status === 404) {
    const err = new Error('This tutor endpoint is not deployed yet.');
    err.offline = true;
    throw err;
  }
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string' && body.detail) detail = body.detail;
    } catch (e) {
      // non-JSON error body — keep the status-code message
    }
    const err = new Error(detail);
    err.offline = res.status >= 500;
    throw err;
  }
  return res.json();
}

/**
 * The caller's newest active session for `agentId`, or `null` if they have
 * none. Callers that can't resume (offline, endpoint missing) should treat
 * a thrown `.offline` error the same as a `null` result — there's simply
 * nothing to resume, not a broken page.
 */
export async function getActiveSession(agentId) {
  return tutorGet(`/sessions/active?agentId=${encodeURIComponent(agentId)}`);
}

/** The caller's own recent sessions, newest first, optionally scoped to one agent. */
export async function listSessions(agentId, limit = 10) {
  const params = new URLSearchParams();
  if (agentId) params.set('agentId', agentId);
  if (limit) params.set('limit', limit);
  return tutorGet(`/sessions?${params.toString()}`);
}

/** Mark a session abandoned so the next visit doesn't try to resume it. */
export async function abandonSession(sessionId) {
  let res;
  try {
    res = await fetch(`${config.API_BASE_URL}/api/agent-tutor/sessions/${sessionId}/abandon`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (e) {
    const err = new Error('Could not reach the tutor service.');
    err.offline = true;
    throw err;
  }
  if (!res.ok) {
    const err = new Error(`Couldn't abandon session (${res.status})`);
    err.offline = res.status === 404 || res.status >= 500;
    throw err;
  }
  return res.json();
}
